/**
 * useLiveQuizSession Hook
 * 
 * Manages the live quiz session state and RTDB subscriptions.
 * Provides a clean interface for managing quiz data without exposing
 * Firebase internals to the view layer.
 * 
 * Uses unifiedDataService for all database operations.
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  LiveSessionService, 
  RealtimeDBService,
  updateInstance as updateInstanceFirestore
} from '../firebase/unifiedDataService';
import { useUnifiedAuth } from '../context/UnifiedAuthContext';
import { normalizeQuestion } from '../utils/llm-generator/questionTransformers';
import { Logger } from '../utils/logger';

const log = Logger.create('useLiveQuizSession');

/**
 * @typedef {Object} QuizInstance
 * @property {string} id - Instance ID
 * @property {string} title - Quiz title
 * @property {string} [sessionCode] - 6-char session code
 * @property {Object} settings - Quiz settings
 * @property {Array} questions - Quiz questions
 * @property {number} currentQuestionIndex - Current question (-1 = lobby)
 * @property {boolean} quizStarted - Whether quiz has started
 * @property {boolean} showResults - Show result counts
 * @property {boolean} showCorrectAnswer - Reveal correct answer
 * @property {boolean} answersLocked - Prevent new answers
 */

/**
 * @typedef {Object} SessionState
 * @property {QuizInstance} instance - Quiz instance data
 * @property {Array} questions - Normalized questions
 * @property {Object} settings - Quiz settings
 * @property {Object} participants - { odId: participantData }
 * @property {Object} allAnswers - { questionIndex: { odId: answerData } }
 * @property {Object} currentAnswers - Answers for current question
 * @property {boolean} loading - Loading state
 * @property {string} error - Error message
 * @property {number} serverTimeOffset - Server time offset in ms
 */

/**
 * Hook to manage live quiz session
 * 
 * @param {string} instanceId - Instance ID or short session code
 * @returns {SessionState & SessionActions}
 */
export function useLiveQuizSession(instanceId) {
  const { user } = useUnifiedAuth();
  
  // Resolved instance ID (from short code)
  const [resolvedInstanceId, setResolvedInstanceId] = useState(null);
  
  // Instance data
  const [instance, setInstance] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [settings, setSettings] = useState({});
  
  // Participants & Answers
  const [participants, setParticipants] = useState({});
  const [allAnswers, setAllAnswers] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  
  // Quiz state
  const [quizStarted, setQuizStarted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [answersLocked, setAnswersLocked] = useState(false);
  
  // Loading/Error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Server time offset (for timer sync)
  const [serverTimeOffset, setServerTimeOffset] = useState(0);

  // ============================================
  // SUBSCRIPTIONS
  // ============================================
  
  // Subscribe to server time offset
  useEffect(() => {
    const unsubscribe = RealtimeDBService.subscribeServerTimeOffset((offset) => {
      log.debug('Server time offset:', offset, 'ms');
      setServerTimeOffset(offset);
    });
    return unsubscribe;
  }, []);
  
  // Resolve short code to instance ID
  useEffect(() => {
    if (!instanceId) return;

    const resolveCode = async () => {
      // Check if it's a short code (≤8 chars, no dash prefix)
      const isShortCode = instanceId.length <= 8 && !instanceId.startsWith('-');

      if (isShortCode) {
        const sessionData = await LiveSessionService.getByCode(instanceId);
        if (sessionData) {
          setResolvedInstanceId(sessionData.instanceId);
        } else {
          // Maybe it's a valid instance ID
          setResolvedInstanceId(instanceId);
        }
      } else {
        setResolvedInstanceId(instanceId);
      }
    };

    resolveCode();
  }, [instanceId]);
  
  // Subscribe to instance data
  useEffect(() => {
    if (!user || !resolvedInstanceId) return;

    setLoading(true);
    setError(null);

    // Subscribe to main instance data
    const unsubscribeInstance = LiveSessionService.subscribe(
      user.uid, 
      resolvedInstanceId,
      (data) => {
        if (data) {
          setInstance({ id: resolvedInstanceId, ...data });
          
          // Normalize questions
          const rawQuestions = data.questions || [];
          const normalizedQuestions = rawQuestions.map(q => normalizeQuestion(q));
          setQuestions(normalizedQuestions);
          
          // Set settings and state
          setSettings(data.settings || {});
          setCurrentQuestionIndex(data.currentQuestionIndex ?? -1);
          setQuizStarted(data.currentQuestionIndex >= 0);
          setShowResults(data.showResults || false);
          setShowCorrectAnswer(data.showCorrectAnswer || false);
          setAnswersLocked(data.answersLocked || false);
        }
        setLoading(false);
      }
    );

    // Subscribe to participants
    const unsubscribeParticipants = RealtimeDBService.subscribe(
      `quiz_instances/${user.uid}/${resolvedInstanceId}/participants`,
      (data) => {
        setParticipants(data || {});
      }
    );

    // Subscribe to answers
    const unsubscribeAnswers = RealtimeDBService.subscribe(
      `quiz_instances/${user.uid}/${resolvedInstanceId}/answers`,
      (data) => {
        setAllAnswers(data || {});
      }
    );

    return () => {
      // Cleanup subscriptions
      if (typeof unsubscribeInstance === 'function') unsubscribeInstance();
      if (typeof unsubscribeParticipants === 'function') unsubscribeParticipants();
      if (typeof unsubscribeAnswers === 'function') unsubscribeAnswers();
    };
  }, [user, resolvedInstanceId]);

  // ============================================
  // COMPUTED VALUES
  // ============================================
  
  // Current answers for the current question
  const currentAnswers = allAnswers[currentQuestionIndex] || {};
  
  // Participant count
  const participantCount = Object.keys(participants).length;
  
  // Current question
  const currentQuestion = questions[currentQuestionIndex] || null;

  // ============================================
  // ACTIONS
  // ============================================
  
  /**
   * Update instance state in Firebase
   * @param {Object} updates - Fields to update
   */
  const updateQuizState = useCallback(async (updates) => {
    if (!user || !resolvedInstanceId) return;

    try {
      await RealtimeDBService.update(
        `quiz_instances/${user.uid}/${resolvedInstanceId}`,
        {
          ...updates,
          lastModified: Date.now()
        }
      );
    } catch (err) {
      log.error('Update error:', err);
      setError('Fehler beim Aktualisieren des Quiz-Status');
    }
  }, [user, resolvedInstanceId]);

  /**
   * Start the quiz (leave lobby)
   */
  const startQuiz = useCallback(async () => {
    await updateQuizState({
      quizStarted: true,
      currentQuestionIndex: 0
    });
    setQuizStarted(true);
    setCurrentQuestionIndex(0);
  }, [updateQuizState]);

  /**
   * End the quiz - with automatic score calculation for all unevaluated answers
   * 
   * This ensures that:
   * 1. All participant answers are scored, even if "Richtige anzeigen" wasn't clicked
   * 2. Gamification scores are correctly calculated for all questions
   * 3. Streak bonus is correctly calculated in sequential order
   */
  const endQuiz = useCallback(async () => {
    // Only perform auto-evaluation if gamification is enabled
    const enableGamification = settings?.enableGamification ?? false;
    
    if (enableGamification && questions.length > 0) {
      log.info('Starting auto-evaluation for quiz end...');
      
      const basePoints = settings?.basePointsPerQuestion || 100;
      const enableTimeBonus = settings?.enableTimeBonus ?? false;
      const enableStreakBonus = settings?.enableStreakBonus ?? false;
      const streakBonusPoints = settings?.streakBonusPoints ?? 50;
      const timeLimitSeconds = settings?.timerDuration || settings?.questionTimerSeconds || 30;
      
      // Time multiplier calculation - MUST MATCH quizView.jsx exactly!
      // Under 5 sec = 2x, At limit = 0.5x, Linear interpolation between
      const calculateTimeMultiplier = (responseTimeMs, timeLimitSec = 30) => {
        const responseTimeSec = responseTimeMs / 1000;
        if (responseTimeSec <= 5) return 2.0;
        if (responseTimeSec >= timeLimitSec) return 0.5;
        return 2.0 - (responseTimeSec - 5) * (1.5 / (timeLimitSec - 5));
      };
      
      // Calculate scores for each participant based on ALL their answers
      const participantUpdates = {};
      
      Object.entries(participants).forEach(([participantId, participant]) => {
        let totalScore = 0;
        let correctCount = 0;
        let maxStreak = 0;
        let currentStreak = 0;
        
        // Go through all questions IN ORDER for correct streak calculation
        questions.forEach((question, questionIndex) => {
          const questionAnswers = allAnswers[questionIndex] || {};
          const answer = questionAnswers[participantId];
          
          if (answer && answer.isCorrect !== undefined) {
            if (answer.isCorrect) {
              correctCount++;
              
              // Calculate points with time bonus
              let pointsEarned = basePoints;
              if (enableTimeBonus && answer.responseTimeMs) {
                const multiplier = calculateTimeMultiplier(answer.responseTimeMs, timeLimitSeconds);
                pointsEarned = Math.round(basePoints * multiplier);
              }
              
              // Add streak bonus (current streak BEFORE this question)
              if (enableStreakBonus && currentStreak > 0) {
                pointsEarned += currentStreak * streakBonusPoints;
              }
              
              totalScore += pointsEarned;
              currentStreak++;
              maxStreak = Math.max(maxStreak, currentStreak);
            } else {
              currentStreak = 0;
            }
          }
        });
        
        // Update participant with calculated total
        participantUpdates[participantId] = {
          totalScore: totalScore,
          correctCount: correctCount,
          maxStreak: maxStreak,
          currentStreak: 0 // Reset at end
        };
      });
      
      // Batch update all participants
      const updatePromises = Object.entries(participantUpdates).map(([participantId, updates]) => 
        RealtimeDBService.update(
          `quiz_instances/${user.uid}/${resolvedInstanceId}/participants/${participantId}`,
          updates
        )
      );
      
      await Promise.all(updatePromises);
      log.info(`Auto-evaluation complete: Updated ${Object.keys(participantUpdates).length} participants`);
    }
    
    // Mark quiz as completed in BOTH Realtime DB and Firestore
    const completionData = {
      status: 'completed',
      endedAt: Date.now()
    };
    
    // Update Realtime DB
    await updateQuizState(completionData);
    
    // Update Firestore so dashboard shows correct status
    try {
      await updateInstanceFirestore(resolvedInstanceId, completionData);
      log.info('Quiz marked as completed in Firestore');
    } catch (err) {
      log.error('Failed to update Firestore status:', err);
      // Don't throw - RTDB update succeeded
    }
  }, [updateQuizState, settings, questions, participants, allAnswers, user, resolvedInstanceId]);

  /**
   * Reset quiz to lobby state
   */
  const resetQuiz = useCallback(async () => {
    // Reset local state
    setCurrentQuestionIndex(-1);
    setQuizStarted(false);
    setShowResults(false);
    setShowCorrectAnswer(false);
    setAnswersLocked(false);
    
    // Reset Firebase state
    await updateQuizState({
      currentQuestionIndex: -1,
      quizStarted: false,
      showResults: false,
      showCorrectAnswer: false,
      answersLocked: false,
      timerState: null
    });

    // Clear answers
    await RealtimeDBService.remove(
      `quiz_instances/${user.uid}/${resolvedInstanceId}/answers`
    );

    // Reset participant scores
    const resetPromises = Object.keys(participants).map(async (odId) => {
      await RealtimeDBService.update(
        `quiz_instances/${user.uid}/${resolvedInstanceId}/participants/${odId}`,
        {
          score: 0,
          currentStreak: 0,
          maxStreak: 0,
          jokersUsed: [],
          jokersAvailable: settings.jokerCount || 1
        }
      );
    });

    await Promise.all(resetPromises);
  }, [user, resolvedInstanceId, updateQuizState, participants, settings]);

  /**
   * Navigate to a question
   * @param {number} index - Question index (-1 for lobby)
   */
  const goToQuestion = useCallback(async (index) => {
    if (index < -1 || index >= questions.length) return;
    
    setCurrentQuestionIndex(index);
    setShowResults(false);
    setShowCorrectAnswer(false);
    setAnswersLocked(false);
    
    await updateQuizState({
      currentQuestionIndex: index,
      showResults: false,
      showCorrectAnswer: false,
      answersLocked: false
    });
    
    return index;
  }, [questions.length, updateQuizState]);

  /**
   * Toggle results display
   */
  const toggleShowResults = useCallback(async () => {
    const newValue = !showResults;
    setShowResults(newValue);
    await updateQuizState({ showResults: newValue });
  }, [showResults, updateQuizState]);

  /**
   * Toggle correct answer reveal
   * Also marks the question as "points awarded" to prevent double points
   */
  const toggleShowCorrectAnswer = useCallback(async () => {
    if (!user || !resolvedInstanceId) return;
    
    const newValue = !showCorrectAnswer;
    setShowCorrectAnswer(newValue);
    
    // When showing correct answer for the first time, mark points as awarded
    if (newValue && currentQuestionIndex !== null && currentQuestionIndex >= 0) {
      const pointsAwardedPath = `quiz_instances/${user.uid}/${resolvedInstanceId}/points_awarded/${currentQuestionIndex}`;
      const alreadyAwarded = await RealtimeDBService.get(pointsAwardedPath);
      
      if (!alreadyAwarded) {
        log.info(`Marking points as awarded for question ${currentQuestionIndex}`);
        await RealtimeDBService.set(pointsAwardedPath, {
          awarded: true,
          timestamp: Date.now()
        });
      }
    }
    
    await updateQuizState({ showCorrectAnswer: newValue });
  }, [showCorrectAnswer, currentQuestionIndex, user, resolvedInstanceId, updateQuizState]);

  /**
   * Toggle answers locked
   */
  const toggleAnswersLocked = useCallback(async (value) => {
    const newValue = value !== undefined ? value : !answersLocked;
    setAnswersLocked(newValue);
    
    if (newValue) {
      await updateQuizState({ 
        answersLocked: true,
        timerState: null // Stop timer for participants
      });
    } else {
      await updateQuizState({ answersLocked: false });
    }
  }, [answersLocked, updateQuizState]);

  /**
   * Update quiz settings
   */
  const updateSettings = useCallback(async (newSettings) => {
    const mergedSettings = { ...settings, ...newSettings };
    setSettings(mergedSettings);
    await updateQuizState({ settings: mergedSettings });
  }, [settings, updateQuizState]);

  /**
   * Remove a participant
   */
  const removeParticipant = useCallback(async (odId) => {
    await RealtimeDBService.remove(
      `quiz_instances/${user.uid}/${resolvedInstanceId}/participants/${odId}`
    );
  }, [user, resolvedInstanceId]);

  // ============================================
  // RETURN
  // ============================================
  
  return {
    // Instance Data
    instance,
    instanceId: resolvedInstanceId,
    questions,
    settings,
    currentQuestion,
    currentQuestionIndex,
    
    // Participants & Answers
    participants,
    participantCount,
    allAnswers,
    currentAnswers,
    
    // Quiz State
    quizStarted,
    showResults,
    showCorrectAnswer,
    answersLocked,
    
    // Loading/Error
    loading,
    error,
    
    // Server Time
    serverTimeOffset,
    
    // Actions
    updateQuizState,
    startQuiz,
    endQuiz,
    resetQuiz,
    goToQuestion,
    toggleShowResults,
    toggleShowCorrectAnswer,
    toggleAnswersLocked,
    updateSettings,
    removeParticipant
  };
}

export default useLiveQuizSession;
